import {
  Banner,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Spinner,
} from '@dawn/ui';
import type { ReactNode } from 'react';

interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: ReactNode;
  /** Label for the confirming action (e.g. "Revoke", "Remove"). */
  confirmLabel: string;
  onConfirm: () => void;
  /** Pending state of the underlying mutation — drives the button spinner. */
  loading?: boolean;
  /** Inline error shown when the action failed; the dialog stays open to retry. */
  error?: string | null;
  'data-testid'?: string;
}

/**
 * Shared confirmation dialog for destructive roster actions (revoke / remove).
 * Holds the loading and error states inline so the user sees the 2-second
 * action resolve in place: spinner while pending, error banner with the dialog
 * still open on failure, and the parent closes it on success.
 */
export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  onConfirm,
  loading = false,
  error = null,
  'data-testid': testId,
}: ConfirmDialogProps) {
  const handleOpenChange = (next: boolean) => {
    // Don't let an outside click / Esc dismiss the dialog mid-request.
    if (!next && loading) return;
    onOpenChange(next);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="gap-5 rounded-xl bg-popover p-6 shadow-2xl sm:max-w-md sm:rounded-xl"
        data-testid={testId}
      >
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        {error && (
          <Banner variant="error" className="rounded-md border" data-testid={`${testId}-error`}>
            {error}
          </Banner>
        )}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={loading}
            data-testid={`${testId}-cancel`}
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={onConfirm}
            disabled={loading}
            leadingIcon={loading ? <Spinner animate size={16} /> : undefined}
            data-testid={`${testId}-confirm`}
          >
            {loading ? 'Working…' : confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
